/**
 * useEmailFirstLogin (TQ #6893 / #6894).
 *
 * Shared email-first login flow: resolve the Keycloak IdP broker alias for an
 * email and redirect straight to that broker (kc_idp_hint), skipping the
 * broker-selection page (Hub-like experience).
 *
 * ADDITIVE — wraps useAuth().login() with idpHint. It never changes the auth
 * context. If idp-resolve fails it shows an error toast and falls back to the
 * broker-selection page (no silent redirect to personal-users).
 *
 * Used by:
 *  - LoginPage (A): manual email entry + "Weiter" button.
 *  - App (B): auto-trigger when an unauth visitor lands with ?login_hint=<email>.
 *
 * Security Fix (TQ #10629): prior to this fix, a failed idp-resolve silently
 * redirected the user to the `personal-users` realm as a catch-all broker.
 * This created a security risk: users could end up in the wrong realm,
 * causing authentication failures or, worst case, cross-realm access.
 * The fix now shows a visible error message and redirects to the broker
 * selection page instead of auto-landing in personal-users.
 */
import { useCallback } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { useToast } from '../components/ui/Toast'
import { IdpResolveAPI } from '../services/idpResolveApi'

export function useEmailFirstLogin() {
  const { login } = useAuth()
  const { showToast } = useToast()

  // Resolve broker for the email, then login(undefined, alias). On any error,
  // show a visible error toast and redirect to the broker-selection page
  // (plain login without idpHint) instead of silently falling back to the
  // catch-all broker.
  const loginWithEmail = useCallback(
    async (email: string): Promise<void> => {
      const trimmed = email.trim()
      if (!trimmed) {
        // No email -> plain login (existing default behaviour, no idpHint).
        login()
        return
      }
      try {
        const alias = await IdpResolveAPI.resolve(trimmed)
        login(undefined, alias)
      } catch {
        // idp-resolve failed -> show error + plain login (broker selection).
        // Security Fix: never silently fallback to personal-users (TQ #10629).
        showToast(
          'error',
          'Realm-Erkennung fehlgeschlagen. Bitte wählen Sie Ihren Anbieter aus.',
          6000
        )
        login()
      }
    },
    [login, showToast]
  )

  return { loginWithEmail }
}
